import type React from 'react'
import { Skeleton } from './ui/skeleton'
import { cn } from '../lib/utils'
import { hyphenateFirstWord, hyphenatesName } from '../lib/hyphenate'
import { cardNameClass, cardNameStyle } from '../lib/cardName'
import type { Artist } from '../types'

/**
 * A card's name line for a set or lineup act with several performers: "A b2b B"
 * with a muted separator and a skeleton for each artist that hasn't loaded yet,
 * or `title` when the billing leads (a named collab, "Skull Machine").
 * Compact cards clamp to `nameLines`. A full-size card's header already clips
 * what doesn't fit; clamping here too (overflow → a new block formatting context
 * beside the floated heart) cut off the name's first letters when the card's
 * status border thickened.
 */
export function SetNameContent({
  artistIds,
  title,
  artists,
  compact,
  nameLines,
}: {
  artistIds: string[]
  title?: string
  artists: Record<string, Artist>
  compact: 'xs' | 'sm' | 'md' | undefined
  nameLines?: number
}) {
  if (!title && artistIds.length <= 1) return <Skeleton className="h-3 w-24" />
  const hyphenate = hyphenatesName(compact, nameLines)
  const content = title
    ? (hyphenate ? hyphenateFirstWord(title) : title)
    : artistIds.flatMap((id, i) => {
      const items: React.ReactNode[] = []
      if (i > 0) items.push(<span key={`sep-${i}`} className="font-normal text-on-image/60">{' b2b '}</span>)
      const artistName = artists[id]?.name
      const shown = i === 0 && artistName && hyphenate ? hyphenateFirstWord(artistName) : artistName
      items.push(shown ? <span key={id}>{shown}</span> : <Skeleton key={id} className="h-3 w-16 inline-block align-middle" />)
      return items
    })
  return (
    <div
      className={cn('font-semibold leading-tight text-on-image select-none', cardNameClass(compact, nameLines))}
      style={cardNameStyle(compact, nameLines)}
    >
      {content}
    </div>
  )
}
